import { getAdditionalAvailability, validateAdditionalQuantity } from '@src/common/additionals';
import { Additional, AdditionalType, Product, Scheduling } from '@src/core/buyerApiClient';
import type { TariffsPerType } from '@src/stores/product';

export type ProductAdditionalFormSlice = {
  checked: boolean;
  input: string;
  quantity?: number;
};

export type AdditionalsSelectionIsCompleteInput = {
  additionals: Additional[];
  product: Product | null | undefined;
  dates: Record<string, { availables: Scheduling[] } | undefined>;
  selectedDate: string | undefined;
  selectedTariffMinutes: number | undefined;
  listOfTariffsPerType: TariffsPerType[];
  getAdditionalFormInput: (additional: Additional) => ProductAdditionalFormSlice;
};

type AdditionalSelectionIsCompleteInput = {
  additional: Additional;
  product: Product;
  dates: Record<string, { availables: Scheduling[] } | undefined>;
  selectedDate: string | undefined;
  selectedTariffMinutes: number | undefined;
  listOfTariffsPerType: TariffsPerType[];
  getAdditionalFormInput: (additional: Additional) => ProductAdditionalFormSlice;
};

function additionalSelectionIsComplete({
  additional,
  product,
  dates,
  selectedDate,
  selectedTariffMinutes,
  listOfTariffsPerType,
  getAdditionalFormInput,
}: AdditionalSelectionIsCompleteInput): boolean {
  const formInput = getAdditionalFormInput(additional);
  if (!formInput.checked) {
    return true;
  }

  if (additional.enableInput && !formInput.input.trim()) {
    return false;
  }

  const { availableAmount, isAvailable } = getAdditionalAvailability(
    dates[selectedDate ?? '']?.availables,
    additional,
    product,
    selectedDate,
    selectedTariffMinutes,
    listOfTariffsPerType,
  );

  if (!isAvailable) {
    return false;
  }

  if (additional.type === AdditionalType.PerQuantity) {
    const { isQuantityValid } = validateAdditionalQuantity({
      quantity: formInput.quantity,
      additional,
      availableAmount,
    });
    if (!isQuantityValid) {
      return false;
    }
  }

  return true;
}

export function additionalsSelectionIsComplete(params: AdditionalsSelectionIsCompleteInput): boolean {
  const { additionals, product } = params;

  if (!product) {
    return false;
  }

  return additionals.every((additional) => additionalSelectionIsComplete({ ...params, additional, product }));
}
